// After a successful SOL -> kySOL conversion, the freshly-minted balance can lag
// on the RPC node we query, so reading the ATA immediately after confirmation
// often reports the pre-conversion balance (delta == 0). Returning 0 there would
// abort the drip deposit even though kySOL was actually minted, orphaning the
// user's funds. This resolver polls until the credit lands, then returns the
// delta; only if it never appears does it fall back to the expected amount.

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export interface ResolveMintedAmountParams {
  /** kySOL balance (in base units) read before the conversion. */
  balanceBefore: bigint;
  /** Reads the current kySOL balance; may throw if the ATA isn't readable yet. */
  fetchBalance: () => Promise<bigint>;
  /** Expected minted amount, used only if the credit never shows up. */
  estimate: number;
  retries?: number;
  delayMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

export async function resolveMintedAmount({
  balanceBefore,
  fetchBalance,
  estimate,
  retries = 6,
  delayMs = 750,
  sleep = defaultSleep,
}: ResolveMintedAmountParams): Promise<number> {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const after = await fetchBalance();
      const delta = Number(after - balanceBefore);
      if (delta > 0) return delta;
    } catch {
      // ATA may not be readable yet; keep polling.
    }
    if (attempt < retries - 1) await sleep(delayMs);
  }
  return estimate;
}
